/**
 * Map authoring + geometry helpers.
 *
 * Maps are built from rectangles rather than hand-written tile arrays so a new
 * office is a few dozen readable lines. The server builds the map once at boot
 * and ships it to clients in `welcome`, so it is the single source of truth.
 */

import { APPEARANCE_SIZES, PLAYER_RADIUS, TILE_SIZE } from './constants.js';
import {
  SOLID_TILES,
  Tile,
  type Appearance,
  type OfficeMap,
  type Surface,
  type TileRect,
  type Zone,
} from './types.js';

/* -------------------------------------------------------------------------- */
/* Grid helpers                                                                */
/* -------------------------------------------------------------------------- */

class Grid {
  readonly cells: number[];

  constructor(
    readonly width: number,
    readonly height: number,
    fill = Tile.Void as number,
  ) {
    this.cells = new Array<number>(width * height).fill(fill);
  }

  set(x: number, y: number, tile: number): void {
    if (x < 0 || y < 0 || x >= this.width || y >= this.height) return;
    this.cells[y * this.width + x] = tile;
  }

  get(x: number, y: number): number {
    if (x < 0 || y < 0 || x >= this.width || y >= this.height) return Tile.Void;
    return this.cells[y * this.width + x] ?? Tile.Void;
  }

  fill(rect: TileRect, tile: number): void {
    for (let y = rect.y; y < rect.y + rect.h; y++) {
      for (let x = rect.x; x < rect.x + rect.w; x++) this.set(x, y, tile);
    }
  }

  outline(rect: TileRect, tile: number): void {
    for (let x = rect.x; x < rect.x + rect.w; x++) {
      this.set(x, rect.y, tile);
      this.set(x, rect.y + rect.h - 1, tile);
    }
    for (let y = rect.y; y < rect.y + rect.h; y++) {
      this.set(rect.x, y, tile);
      this.set(rect.x + rect.w - 1, y, tile);
    }
  }
}

/** Inset a rect by `n` tiles on every side — a room's walls to its floor. */
function inset(rect: TileRect, n = 1): TileRect {
  return { x: rect.x + n, y: rect.y + n, w: rect.w - n * 2, h: rect.h - n * 2 };
}

/* -------------------------------------------------------------------------- */
/* Tile <-> pixel conversion                                                   */
/* -------------------------------------------------------------------------- */

/** Centre of a tile, in pixels. */
export function tileCenter(tx: number, ty: number): { x: number; y: number } {
  return { x: tx * TILE_SIZE + TILE_SIZE / 2, y: ty * TILE_SIZE + TILE_SIZE / 2 };
}

export function toTile(px: number, py: number): { tx: number; ty: number } {
  return { tx: Math.floor(px / TILE_SIZE), ty: Math.floor(py / TILE_SIZE) };
}

export function rectToPixels(rect: TileRect): { x: number; y: number; w: number; h: number } {
  return {
    x: rect.x * TILE_SIZE,
    y: rect.y * TILE_SIZE,
    w: rect.w * TILE_SIZE,
    h: rect.h * TILE_SIZE,
  };
}

export function rectCenterPixels(rect: TileRect): { x: number; y: number } {
  return {
    x: (rect.x + rect.w / 2) * TILE_SIZE,
    y: (rect.y + rect.h / 2) * TILE_SIZE,
  };
}

/* -------------------------------------------------------------------------- */
/* Collision                                                                   */
/* -------------------------------------------------------------------------- */

/** The effective tile at a grid cell: furniture wins over ground. */
export function tileAt(map: OfficeMap, tx: number, ty: number): number {
  if (tx < 0 || ty < 0 || tx >= map.width || ty >= map.height) return Tile.Void;
  const i = ty * map.width + tx;
  const decor = map.decor[i] ?? 0;
  return decor !== 0 ? decor : (map.ground[i] ?? Tile.Void);
}

export function isSolidTile(map: OfficeMap, tx: number, ty: number): boolean {
  return SOLID_TILES.has(tileAt(map, tx, ty));
}

/**
 * Can an avatar centred at (px, py) stand there?
 *
 * Samples the four corners of the avatar's bounding box, which is enough for a
 * radius smaller than a tile and keeps the check cheap enough to run per frame.
 */
export function canStand(map: OfficeMap, px: number, py: number): boolean {
  const r = PLAYER_RADIUS;
  const corners: Array<[number, number]> = [
    [px - r, py - r],
    [px + r, py - r],
    [px - r, py + r],
    [px + r, py + r],
  ];
  for (const [cx, cy] of corners) {
    const { tx, ty } = toTile(cx, cy);
    if (isSolidTile(map, tx, ty)) return false;
  }
  return true;
}

/**
 * Move from a position by (dx, dy), sliding along walls instead of stopping
 * dead. Axes are resolved independently so a diagonal into a wall still slides.
 */
export function resolveMove(
  map: OfficeMap,
  x: number,
  y: number,
  dx: number,
  dy: number,
): { x: number; y: number } {
  let nx = x;
  let ny = y;
  if (dx !== 0 && canStand(map, nx + dx, ny)) nx += dx;
  if (dy !== 0 && canStand(map, nx, ny + dy)) ny += dy;
  return { x: nx, y: ny };
}

/** Nearest standable position to (px, py), searched outwards in rings. */
export function nearestStandable(map: OfficeMap, px: number, py: number): { x: number; y: number } {
  if (canStand(map, px, py)) return { x: px, y: py };
  const origin = toTile(px, py);
  for (let radius = 1; radius < 24; radius++) {
    for (let dy = -radius; dy <= radius; dy++) {
      for (let dx = -radius; dx <= radius; dx++) {
        if (Math.max(Math.abs(dx), Math.abs(dy)) !== radius) continue;
        const c = tileCenter(origin.tx + dx, origin.ty + dy);
        if (canStand(map, c.x, c.y)) return c;
      }
    }
  }
  return tileCenter(map.spawn.x, map.spawn.y);
}

/* -------------------------------------------------------------------------- */
/* Zones + surfaces                                                            */
/* -------------------------------------------------------------------------- */

export function rectContainsPixel(rect: TileRect, px: number, py: number): boolean {
  const { tx, ty } = toTile(px, py);
  return tx >= rect.x && ty >= rect.y && tx < rect.x + rect.w && ty < rect.y + rect.h;
}

/**
 * Which zone a pixel position falls in. Rooms are checked before the commons so
 * an overlapping commons rect never shadows a room.
 */
export function zoneAt(map: OfficeMap, px: number, py: number): Zone | null {
  let commons: Zone | null = null;
  for (const zone of map.zones) {
    if (!rectContainsPixel(zone.rect, px, py)) continue;
    if (zone.kind === 'commons') {
      commons = zone;
      continue;
    }
    return zone;
  }
  return commons;
}

export function zoneById(map: OfficeMap, id: string | null): Zone | null {
  if (!id) return null;
  return map.zones.find((z) => z.id === id) ?? null;
}

export function surfaceById(map: OfficeMap, id: string): Surface | null {
  return map.surfaces.find((s) => s.id === id) ?? null;
}

/** Distance from a pixel position to the nearest edge of a tile rect. */
export function distanceToRect(rect: TileRect, px: number, py: number): number {
  const r = rectToPixels(rect);
  const dx = Math.max(r.x - px, 0, px - (r.x + r.w));
  const dy = Math.max(r.y - py, 0, py - (r.y + r.h));
  return Math.hypot(dx, dy);
}

/** The surface a player standing at (px, py) can reach, nearest first. */
export function nearestSurface(
  map: OfficeMap,
  px: number,
  py: number,
  maxDistance: number,
): Surface | null {
  let best: Surface | null = null;
  let bestDistance = Infinity;
  for (const surface of map.surfaces) {
    const d = distanceToRect(surface.rect, px, py);
    if (d <= maxDistance && d < bestDistance) {
      best = surface;
      bestDistance = d;
    }
  }
  return best;
}

/* -------------------------------------------------------------------------- */
/* The default office                                                          */
/* -------------------------------------------------------------------------- */

interface RoomSpec {
  id: string;
  name: string;
  kind: Zone['kind'];
  /** Outer rect including walls, in tiles. */
  rect: TileRect;
  floor: number;
  audio: Zone['audio'];
  description: string;
  /** Doorways carved into the walls, in tile coordinates. */
  doors: Array<{ x: number; y: number }>;
}

const WIDTH = 48;
const HEIGHT = 34;

const ROOMS: RoomSpec[] = [
  {
    id: 'meeting-a',
    name: 'Meeting Room A',
    kind: 'meeting',
    rect: { x: 0, y: 0, w: 15, h: 12 },
    floor: Tile.Carpet,
    audio: { mode: 'zone', capacity: 8 },
    description: 'Closed room. Everyone inside hears everyone inside.',
    doors: [
      { x: 14, y: 5 },
      { x: 14, y: 6 },
    ],
  },
  {
    id: 'meeting-b',
    name: 'Meeting Room B',
    kind: 'meeting',
    rect: { x: 0, y: 11, w: 15, h: 12 },
    floor: Tile.Carpet,
    audio: { mode: 'zone', capacity: 8 },
    description: 'Closed room. Good for pairing.',
    doors: [
      { x: 14, y: 16 },
      { x: 14, y: 17 },
    ],
  },
  {
    id: 'quiet',
    name: 'Quiet Zone',
    kind: 'quiet',
    rect: { x: 0, y: 22, w: 15, h: 12 },
    floor: Tile.Wood,
    audio: { mode: 'proximity', capacity: 6 },
    description: 'Heads-down desks. Keep it to text.',
    doors: [
      { x: 14, y: 27 },
      { x: 14, y: 28 },
    ],
  },
  {
    id: 'workshop',
    name: 'Workshop',
    kind: 'workshop',
    rect: { x: 32, y: 0, w: 16, h: 12 },
    floor: Tile.Wood,
    audio: { mode: 'zone', capacity: 12 },
    description: 'Whiteboard wall for sketching things out.',
    doors: [
      { x: 32, y: 5 },
      { x: 32, y: 6 },
    ],
  },
  {
    id: 'lounge',
    name: 'Lounge',
    kind: 'social',
    rect: { x: 32, y: 11, w: 16, h: 12 },
    floor: Tile.Rug,
    audio: { mode: 'proximity' },
    description: 'Couches and coffee. Voices carry only as far as you walk.',
    doors: [
      { x: 32, y: 16 },
      { x: 32, y: 17 },
    ],
  },
  {
    id: 'library',
    name: 'Library',
    kind: 'library',
    rect: { x: 32, y: 22, w: 16, h: 12 },
    floor: Tile.Wood,
    audio: { mode: 'proximity', capacity: 6 },
    description: 'Decision log and reading list, pinned along the far wall.',
    doors: [
      { x: 32, y: 27 },
      { x: 32, y: 28 },
    ],
  },
];

const COMMONS: Zone = {
  id: 'commons',
  name: 'The Commons',
  kind: 'commons',
  rect: { x: 15, y: 1, w: 17, h: 32 },
  channelId: 'zone:commons',
  audio: { mode: 'proximity' },
  description: 'Open floor. You hear whoever you are standing near.',
};

/** Builds the bundled office. Deterministic, so client and server agree. */
export function createDefaultOffice(): OfficeMap {
  const ground = new Grid(WIDTH, HEIGHT, Tile.Floor);
  const decor = new Grid(WIDTH, HEIGHT, 0);

  // Outer shell.
  ground.outline({ x: 0, y: 0, w: WIDTH, h: HEIGHT }, Tile.Wall);

  // Rooms: walls, then floor, then doorways punched back through the walls.
  for (const room of ROOMS) {
    ground.outline(room.rect, Tile.Wall);
    ground.fill(inset(room.rect), room.floor);
    for (const door of room.doors) ground.set(door.x, door.y, Tile.Door);
  }

  // A few windows along the outer wall so the place reads as a building.
  for (const y of [3, 4, 19, 20, 30, 31]) ground.set(0, y, Tile.Window);
  for (const y of [3, 4, 19, 20, 30, 31]) ground.set(WIDTH - 1, y, Tile.Window);
  for (const x of [20, 21, 26, 27]) {
    ground.set(x, 0, Tile.Window);
    ground.set(x, HEIGHT - 1, Tile.Window);
  }

  const surfaces: Surface[] = [];

  /** A conference table with chairs on the long sides. */
  const conferenceTable = (rect: TileRect) => {
    decor.fill(rect, Tile.Table);
    for (let x = rect.x; x < rect.x + rect.w; x++) {
      decor.set(x, rect.y - 1, Tile.Chair);
      decor.set(x, rect.y + rect.h, Tile.Chair);
    }
  };

  /** A wall-mounted board plus the surface record that makes it interactive. */
  const mountBoard = (
    id: string,
    label: string,
    zoneId: string,
    rect: TileRect,
    facing: Surface['facing'],
    kind: Surface['kind'],
  ) => {
    decor.fill(rect, kind === 'whiteboard' ? Tile.Whiteboard : Tile.NoteBoard);
    surfaces.push({ id, kind, label, zoneId, rect, facing });
  };

  // --- Meeting Room A -------------------------------------------------------
  conferenceTable({ x: 4, y: 5, w: 7, h: 2 });
  decor.set(2, 2, Tile.Plant);
  decor.set(12, 9, Tile.Plant);
  mountBoard('board-meeting-a', 'Meeting A notes', 'meeting-a', { x: 5, y: 1, w: 5, h: 1 }, 'down', 'noteboard');

  // --- Meeting Room B -------------------------------------------------------
  conferenceTable({ x: 4, y: 16, w: 7, h: 2 });
  decor.set(2, 13, Tile.Plant);
  decor.set(12, 20, Tile.Plant);
  mountBoard('board-meeting-b', 'Meeting B notes', 'meeting-b', { x: 5, y: 12, w: 5, h: 1 }, 'down', 'noteboard');

  // --- Quiet Zone: rows of single desks ------------------------------------
  for (let row = 0; row < 3; row++) {
    const y = 25 + row * 3;
    for (let i = 0; i < 3; i++) {
      const x = 3 + i * 4;
      decor.fill({ x, y, w: 2, h: 1 }, Tile.Desk);
      decor.set(x, y + 1, Tile.Chair);
      decor.set(x + 1, y + 1, Tile.Chair);
    }
  }
  mountBoard('board-quiet', 'Focus log', 'quiet', { x: 5, y: 23, w: 4, h: 1 }, 'down', 'noteboard');

  // --- Workshop: whiteboard wall + open floor ------------------------------
  mountBoard('whiteboard-workshop', 'Workshop whiteboard', 'workshop', { x: 35, y: 1, w: 9, h: 1 }, 'down', 'whiteboard');
  mountBoard('board-workshop', 'Workshop parking lot', 'workshop', { x: 45, y: 4, w: 1, h: 4 }, 'left', 'noteboard');
  conferenceTable({ x: 37, y: 6, w: 5, h: 2 });
  decor.set(34, 10, Tile.Plant);

  // --- Lounge: couches around a coffee table -------------------------------
  decor.fill({ x: 36, y: 14, w: 4, h: 1 }, Tile.Couch);
  decor.fill({ x: 36, y: 19, w: 4, h: 1 }, Tile.Couch);
  decor.fill({ x: 37, y: 16, w: 2, h: 2 }, Tile.Table);
  decor.fill({ x: 43, y: 13, w: 3, h: 1 }, Tile.Counter);
  decor.set(46, 21, Tile.Plant);
  decor.set(34, 13, Tile.Plant);
  mountBoard('board-lounge', 'Watercooler board', 'lounge', { x: 42, y: 21, w: 4, h: 1 }, 'up', 'noteboard');
  // Declared now, lights up when screen sharing lands. Kept clear of the
  // doorway at (32, 16)-(32, 17) so it cannot wall the room off.
  const loungeScreen: TileRect = { x: 40, y: 12, w: 4, h: 1 };
  decor.fill(loungeScreen, Tile.Screen);
  surfaces.push({
    id: 'screen-lounge',
    kind: 'screen',
    label: 'Lounge screen',
    zoneId: 'lounge',
    rect: loungeScreen,
    facing: 'down',
  });

  // --- Library: boards along the far wall ----------------------------------
  mountBoard('board-decisions', 'Decision log', 'library', { x: 46, y: 24, w: 1, h: 4 }, 'left', 'noteboard');
  mountBoard('board-reading', 'Reading list', 'library', { x: 46, y: 29, w: 1, h: 3 }, 'left', 'noteboard');
  for (let i = 0; i < 2; i++) {
    const y = 26 + i * 4;
    decor.fill({ x: 35, y, w: 4, h: 1 }, Tile.Desk);
    for (let x = 35; x < 39; x++) decor.set(x, y + 1, Tile.Chair);
  }
  decor.set(34, 32, Tile.Plant);

  // --- Commons: desk clusters, plants, the main board ----------------------
  for (let cluster = 0; cluster < 3; cluster++) {
    const y = 6 + cluster * 9;
    decor.fill({ x: 19, y, w: 4, h: 2 }, Tile.Desk);
    for (let x = 19; x < 23; x++) {
      decor.set(x, y - 1, Tile.Chair);
      decor.set(x, y + 2, Tile.Chair);
    }
    decor.fill({ x: 26, y, w: 3, h: 2 }, Tile.Desk);
    for (let x = 26; x < 29; x++) {
      decor.set(x, y - 1, Tile.Chair);
      decor.set(x, y + 2, Tile.Chair);
    }
  }
  decor.set(16, 2, Tile.Plant);
  decor.set(30, 2, Tile.Plant);
  decor.set(16, 31, Tile.Plant);
  decor.set(30, 31, Tile.Plant);
  mountBoard('board-standup', 'Standup board', 'commons', { x: 22, y: 1, w: 4, h: 1 }, 'down', 'noteboard');

  const zones: Zone[] = [
    ...ROOMS.map<Zone>((room) => ({
      id: room.id,
      name: room.name,
      kind: room.kind,
      rect: inset(room.rect),
      channelId: `zone:${room.id}`,
      audio: room.audio,
      description: room.description,
    })),
    COMMONS,
  ];

  const map: OfficeMap = {
    id: 'default-office',
    name: 'The Office',
    width: WIDTH,
    height: HEIGHT,
    tileSize: TILE_SIZE,
    ground: ground.cells,
    decor: decor.cells,
    spawn: { x: 24, y: 29 },
    zones,
    surfaces,
  };

  // Authoring guard: a spawn inside furniture is an easy mistake to make.
  const spawnPixel = tileCenter(map.spawn.x, map.spawn.y);
  if (!canStand(map, spawnPixel.x, spawnPixel.y)) {
    const fixed = nearestStandable(map, spawnPixel.x, spawnPixel.y);
    const { tx, ty } = toTile(fixed.x, fixed.y);
    map.spawn = { x: tx, y: ty };
  }

  return map;
}

/** A random but plausible character, for someone who has not chosen one. */
export function randomAppearance(): Appearance {
  const roll = (n: number) => Math.floor(Math.random() * n);
  return {
    skin: roll(APPEARANCE_SIZES.skin),
    hair: roll(APPEARANCE_SIZES.hair),
    hairColor: roll(APPEARANCE_SIZES.hairColor),
    shirt: roll(APPEARANCE_SIZES.shirt),
    pants: roll(APPEARANCE_SIZES.pants),
  };
}
